import ApiQueue from "@/libraries/ApiQueue";
import FallbackDB from "@/libraries/localStorage";
import { isServerVersionNewer } from "@/helpers/isServerVersionNewer";
import { TaskModel } from "@/models/TaskModel";
import { ParamModel } from "@/models/ParamModel";
import { UserModel } from "@/models/UserModel";
import { ShiftModel } from "@/models/ShiftModel";
import { ImageModel } from "@/models/ImageModel";

export const shifts = {
    namespaced: true,
    state() {
        return {
            TICK_TIME: 1e4,

            loading: false,
            loadingError: false,

            DB_tasks: [],
            DB_params: [],
            DB_shifts: [],

            canUseCache: false,
        }
    },
    getters: {
        getTasks(state) {
            return state.DB_tasks;
        },
        getParams(state) {
            return state.DB_params;
        },
        getShifts(state) {
            return state.DB_shifts;
        },
    },
    mutations: {
        setTasks(state, value) {
            state.DB_tasks = value;
        },
        setParams(state, value) {
            state.DB_params = value;
        },
        setShifts(state, value) {
            state.DB_shifts = value;
        },
        setCanUseCache(state, value) {
            state.canUseCache = value;
        },
    },
    actions: {
        async _loop({state}) {
            if (!UserModel.isAuth()) {
                return;
            }

            const mergeWithLocalShifts = shifts => {
                for (let apiData of shifts) {
                    const m = new ShiftModel(apiData.id);
                    if (!isServerVersionNewer(m, apiData)) {
                        continue;
                    }
                    m.setData(apiData);
                    m.setDataToDB(apiData.id);
                }
                return shifts.map(v => v.id);
            }
            const mergeWithLocalTasks = tasks => {
                for (let apiData of tasks) {
                    const m = new TaskModel(apiData.id);
                    if (!isServerVersionNewer(m, apiData)) {
                        continue;
                    }
                    m.setData(apiData);
                    m.setDataToDB(apiData.id);
                }
                return tasks.map(v => v.id);
            }
            const mergeWithLocalParams = params => {
                for (let apiData of params) {
                    const m = new ParamModel(apiData.id);
                    if (!isServerVersionNewer(m, apiData)) {
                        continue;
                    }
                    m.setData(apiData);
                    m.setDataToDB(apiData.id);
                }
                return params.map(v => v.id);
            }
            const getTasksFromShifts = shifts => {
                return shifts.reduce( (accum, shift) => accum.concat(shift.tasks), [] )
            }
            const getParamsFromShifts = shifts => {
                return shifts.reduce( (accum, shift) => accum.concat(shift.params), [] )
            }
            const cleanUpDBs = mustBeSave => { // Удаляет те данные из shifts/tasks/params данные по которым больше не нужны в локальной БД. Это те которые уже синхронизированы и при этом данные от сервера по ним больше не приходят
                const removeUselessDataFromLocalStorage = () => { // Удаление более ненужных смен, задач и параметров
                    const arr = {
                        shifts: (new ShiftModel()).getList(),
                        tasks: (new TaskModel()).getList(),
                        params: (new ParamModel()).getList(),
                    };
                    for (let table in arr) {
                        const data = arr[table];
                        for (let m of data) {
                            // Если присылается с сервера, значит должна отображаться
                            if (mustBeSave[table].includes(+m.id)) {
                                continue;
                            }
                            // Если завершена позже чем последняя синхронизация, значит есть что ещё закоммитить по ней
                            if (m.id
                                && m.sync_time
                                && m.done_time
                                && m.sync_time < m.done_time) {
                                continue;
                            }
                            m.delete();
                        }
                    }
                }
                const collectImageWhichWeMustSave = () => { // Собираем список картинок которые нужно оставить
                    const needSaveImages = [];
                    for (const m of (new TaskModel()).getList()) {
                        if (!m.photo_1.includes('ref_')) {
                            continue;
                        }
                        needSaveImages.push( parseInt(m.photo_1.replace('ref_', '')) );
                    }
                    for (const m of (new ParamModel()).getList()) {
                        if (!m.value.toString().includes('ref_')) {
                            continue;
                        }
                        needSaveImages.push( parseInt(m.value.replace('ref_', '')) );
                    }
                    for (const m of (new UserModel()).getList()) {
                        if (!m.avatar.includes('ref_')) {
                            continue;
                        }
                        needSaveImages.push( parseInt(m.avatar.replace('ref_', '')) );
                    }
                    return needSaveImages;
                }
                const removeUselessImages = needSaveImages => { // Удаление картинок которые более не будут использоваться
                    setTimeout(async () => {
                        const all = await (new ImageModel()).getList();
                        for (const m of all) {
                            await m.init();
                            if (needSaveImages.includes(m.id)) {
                                continue;
                            }
                            m.delete();
                        }
                    }, 0);
                }

                // Real function code:
                removeUselessDataFromLocalStorage();
                removeUselessImages(
                    collectImageWhichWeMustSave()
                )
            }

            state.loading = state.loadingError = false;
            try { // Грузим shifts с сервера
                state.loading = true;

                const mustBeSave = {};

                const shifts = await ApiQueue.get('shifts', [], ApiQueue.REPLACE_EQUAL); // Получили данные по текущим shifts/tasks/params от API
                mustBeSave.shifts = mergeWithLocalShifts(shifts); // Объединяем с локальными (что-то на local-ке может быть новее, например если смену закрыли, но это ещё не закоммичено)

                const tasks = getTasksFromShifts(shifts);
                mustBeSave.tasks = mergeWithLocalTasks(tasks);

                const params = getParamsFromShifts(shifts);
                mustBeSave.params =mergeWithLocalParams(params);

                cleanUpDBs(mustBeSave);

                state.canUseCache = true;
            } catch (e) {
                console.error('Error loading shifts:', e);
                state.loadingError = true;
            } finally {
                state.loading = false;
            }
        },
        async loop({dispatch}) { // Загружает/обновляет список задач и параметров для юзера с сервера (после этого метода в localStorage появится список задач)
            if (this.locked) {
                return;
            }
            this.locked = true;
            await dispatch('_loop');
            this.locked = false;
        },
        restore({commit}) {
            commit('setShifts', FallbackDB.restore('shifts'));
            commit('setTasks', FallbackDB.restore('tasks'));
            commit('setParams', FallbackDB.restore('params'));
        },
        init({state, dispatch}) { // Вызывает loop каждые TICK_TIME секунд (если прошлый вызов уже завершился, иначе ждёт)
            setInterval(() => dispatch('loop'), state.TICK_TIME);
            dispatch('loop');
        },
    }
}